import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, Download, Eye, FileText, Clock, User } from 'lucide-react';
import { formatIST } from '@/lib/utils';

export default function AttachmentGrid({
  attachments = [],
  categories = [],
  onView,
  onDelete,
  canDelete = () => false,
  loading = false,
  emptyMessage = 'No attachments yet',
}) {
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[cat.id] = cat;
    });
    return map;
  }, [categories]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="aspect-square bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <FileText className="h-12 w-12 mx-auto text-slate-300 mb-2" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {attachments.map((attachment) => {
        const category = categoryMap[attachment.category_id];
        const isImage = attachment.file_kind === 'image';
        const isPdf = attachment.file_kind === 'pdf';

        return (
          <div
            key={attachment.id}
            className="group relative bg-slate-50 rounded-lg overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all"
          >
            {/* Thumbnail */}
            <div
              className="aspect-square cursor-pointer relative"
              onClick={() => onView?.(attachment)}
            >
              {attachment.thumbnail_url ? (
                <img
                  src={attachment.thumbnail_url}
                  alt={attachment.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : isPdf ? (
                <div className="w-full h-full flex items-center justify-center bg-red-50">
                  <FileText className="h-12 w-12 text-red-400" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-100">
                  <FileText className="h-12 w-12 text-slate-300" />
                </div>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Eye className="h-8 w-8 text-white" />
              </div>

              {/* Inherited Badge */}
              {attachment.is_inherited && (
                <div className="absolute top-2 left-2">
                  <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700">
                    Inherited
                  </Badge>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-2">
              {/* Category Badge */}
              {category && (
                <Badge
                  className="text-[10px] mb-1"
                  style={{
                    backgroundColor: `${category.color_hex}15`,
                    color: category.color_hex,
                    borderColor: `${category.color_hex}30`,
                  }}
                >
                  {category.name}
                </Badge>
              )}

              {/* Filename */}
              <p className="text-xs font-medium text-slate-700 truncate" title={attachment.original_filename}>
                {attachment.original_filename}
              </p>

              {/* Caption */}
              {attachment.caption && (
                <p className="text-[10px] text-slate-500 truncate mt-0.5" title={attachment.caption}>
                  {attachment.caption}
                </p>
              )}

              {/* Meta */}
              <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                <span className="flex items-center gap-0.5">
                  <User className="h-3 w-3" />
                  {attachment.uploaded_by_name || attachment.uploaded_by_role}
                </span>
                <span className="flex items-center gap-0.5">
                  <Clock className="h-3 w-3" />
                  {formatIST(attachment.uploaded_at_ist, { compact: true })}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 mt-2">
                {attachment.download_url && (
                  <a
                    href={attachment.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="outline" size="sm" className="w-full h-7 text-xs">
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </Button>
                  </a>
                )}
                {canDelete(attachment) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(attachment);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
